import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '@/providers/AuthProvider'
import { usersApi } from '@/api/usersApi'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { PageHeading } from '@/components/layout/PageHeading'
import { SigningPasswordForm } from '@/components/signature/SigningPasswordForm'
import { OfflineSigningKeyCard } from '@/components/signature/OfflineSigningKeyCard'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { usePrimaryDescription } from '@/hooks/useMesProducts'
import type { PrimaryDescription } from '@/lib/mesProducts'

export function ProfilePage() {
  const { user, email, refresh } = useAuth()

  return (
    <div className='mx-auto flex max-w-xl flex-col gap-6 p-6'>
      <PageHeading title='My profile' description={email} />
      <ProfileCard
        initialFirstName={user?.firstName ?? ''}
        initialLastName={user?.lastName ?? ''}
        initialUsername={user?.username ?? ''}
        onSaved={refresh}
      />
      <ProductDescriptionCard />
      {user?.ssoOnly ? (
        // SSO-only: the Cognito password exists only to sign; there is no login password to change.
        <Card id='signing-password'>
          <CardHeader>
            <CardTitle className='text-base'>Signing password</CardTitle>
            <CardDescription>
              You sign in with SSO. This password is used only for electronic signatures. Minimum 8
              characters with upper, lower and a number.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SigningPasswordForm />
          </CardContent>
        </Card>
      ) : (
        <PasswordCard />
      )}
      <OfflineSigningKeyCard />
    </div>
  )
}

function ProfileCard({
  initialFirstName,
  initialLastName,
  initialUsername,
  onSaved,
}: {
  initialFirstName: string
  initialLastName: string
  initialUsername: string
  onSaved: () => Promise<void>
}) {
  const [firstName, setFirstName] = useState(initialFirstName)
  const [lastName, setLastName] = useState(initialLastName)
  const [username, setUsername] = useState(initialUsername)
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null)
  const [busy, setBusy] = useState(false)

  // useAuth() hydrates `user` async from /auth/me, so the initial values can be
  // empty on first render. Resync the inputs when the props change so a save
  // never PATCHes empty strings over an existing profile.
  useEffect(() => {
    setFirstName(initialFirstName)
  }, [initialFirstName])
  useEffect(() => {
    setLastName(initialLastName)
  }, [initialLastName])
  useEffect(() => {
    setUsername(initialUsername)
  }, [initialUsername])

  const first = firstName.trim()
  const last = lastName.trim()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setStatus(null)
    try {
      await usersApi.updateMe({ firstName: first, lastName: last })
      await onSaved()
      setStatus({ ok: true, msg: 'Profile updated.' })
    } catch (err) {
      setStatus({ ok: false, msg: err instanceof Error ? err.message : 'Could not update profile' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>Name &amp; User ID</CardTitle>
        <CardDescription>Your User ID comes from your email and can be used to sign in.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className='flex flex-col gap-4'>
          <div className='grid grid-cols-2 gap-3'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='p-first'>First name</Label>
              <Input
                id='p-first'
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='p-last'>Last name</Label>
              <Input
                id='p-last'
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='p-username'>User ID</Label>
            {/* Read-only (z8nrz7e46f): assigned from the email, so it is not
                yours to change here — an administrator can, from Users. */}
            <Input id='p-username' value={username} readOnly className='bg-muted text-muted-foreground' />
          </div>
          {status && (
            <p className={status.ok ? 'text-sm text-green-600' : 'text-sm text-destructive'}>
              {status.msg}
            </p>
          )}
          <Button type='submit' disabled={busy || !first || !last} className='self-start'>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function PasswordCard() {
  const [previous, setPrevious] = useState('')
  const [proposed, setProposed] = useState('')
  const [confirm, setConfirm] = useState('')
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const qc = useQueryClient()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setStatus(null)
    if (proposed !== confirm) {
      setStatus({ ok: false, msg: 'New password and confirmation do not match.' })
      return
    }
    setBusy(true)
    try {
      await usersApi.changePassword(previous, proposed)
      // The offline signing keys were locked with the old password: the
      // server retired them, and the card below shows that (z8nrz7fh5c).
      void qc.invalidateQueries({ queryKey: ['signing-keys'] })
      setPrevious('')
      setProposed('')
      setConfirm('')
      setStatus({ ok: true, msg: 'Password changed.' })
    } catch (err) {
      setStatus({ ok: false, msg: err instanceof Error ? err.message : 'Could not change password' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>Change password</CardTitle>
        <CardDescription>Minimum 8 characters with upper, lower and a number.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='p-old'>Current password</Label>
            <PasswordInput id='p-old' required value={previous} onChange={(e) => setPrevious(e.target.value)} />
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='p-new'>New password</Label>
            <PasswordInput id='p-new' required minLength={8} value={proposed} onChange={(e) => setProposed(e.target.value)} />
          </div>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='p-confirm'>Confirm new password</Label>
            <PasswordInput id='p-confirm' required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          {status && (
            <p className={status.ok ? 'text-sm text-green-600' : 'text-sm text-destructive'}>
              {status.msg}
            </p>
          )}
          <Button type='submit' disabled={busy || !previous || proposed.length < 8} className='self-start'>
            {busy ? 'Changing…' : 'Change password'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

/**
 * Which Product Code description reads first for you (z8nrz7fvhx). The two are
 * typically one description in two languages; the other shows on hover. Saved
 * for you alone, in every tenant and on every device.
 */
function ProductDescriptionCard() {
  const { value, update, ready, saveFailed } = usePrimaryDescription()
  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>Product description</CardTitle>
        <CardDescription>
          Which description of a Product Code you read first on MES orders. The other one shows when you hover.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-2'>
        <Select
          value={String(value)}
          onValueChange={v => update(Number(v) as PrimaryDescription)}
          disabled={!ready}
        >
          <SelectTrigger className='w-64' aria-label='Product description shown first'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='1'>Product Description 1</SelectItem>
            <SelectItem value='2'>Product Description 2</SelectItem>
          </SelectContent>
        </Select>
        {saveFailed && <p className='text-sm text-destructive'>Could not save your choice. Try again.</p>}
      </CardContent>
    </Card>
  )
}
